<template>
<div>
  <!--    Loader-->
  <loading-panel :loading="loading"></loading-panel>

  <!--    Breadcrumbs-->
  <breadcrumb style="margin-left: -15px">
    <breadcrumb-item>
      <router-link to="/dashboards" exact>Dashboards</router-link>
    </breadcrumb-item>
    <breadcrumb-item>
      <span>Dashboard settings</span>
    </breadcrumb-item>
  </breadcrumb>

  <div class="row">
    <div class="col">
      <card>
        <div class="row">
          <div class="col">
            <h4 class="card-title">Dashboards</h4>
          </div>
          <div class="col text-right">
            <base-button type="primary" size="sm" v-on:click="openTemplateDrawer()">
              <i class="tim-icons el-icon-plus mr-1"></i> <span class="d-none d-sm-block">Create dashboard</span>
            </base-button>
          </div>
        </div>

        <!--   No data panel -->
        <div v-if="dashboards.length <= 0" class="mt-3" >
          <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
            <h4 class="mt-3">No dashboards have been created.</h4>
          </div>
        </div>

        <div class="row mt-3" v-if="dashboards.length > 0">
          <div class="col">
            <b-table :items="dashboards"
                     :fields="['name', 'created_by', 'actions']">
              <template v-slot:cell(name)="row">
                <p>{{row.item.name}}
                  <i v-if="row.item.pinned" class="tim-icons text-info icon-pin ml-2"></i>
                </p>
                <small>{{row.item.description}}</small>
              </template>
              <template v-slot:cell(created_by)="row">
                <p v-if="row.item.creator_id">{{row.item.first_name}} {{row.item.last_name}}</p>
                <p v-else>System</p>
                <small v-if="row.item.creator_id" style="font-style: italic">
                  {{getLocalDateTime(row.item.created_time)}}
                </small>
              </template>
              <template v-slot:cell(actions)="row">
                <el-dropdown>
                  <base-button
                    simple
                    type="primary"
                    link
                  > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a v-if="!row.item.pinned && row.item.can_write !== 0"
                         class="dropdown-item mt-2 mb-2" href="#" @click="updateDashboard(row.item, $event, 'pin')">
                        <i class="tim-icons icon-pin text-default mr-2"></i> Pin dashboard
                      </a>
                      <a class="dropdown-item mt-2 mb-2" target="_blank" :href="'/dashboards?id=' + row.item.id">
                        <i class="tim-icons icon-link-72 text-default mr-2"></i> Go to dashboard
                      </a>
                      <a v-if="row.item.can_write !== 0" class="dropdown-item mt-2 mb-2" @click="editDashboard(row.item, $event)" href="#">
                        <i class="tim-icons icon-pencil text-default mr-2"></i> Edit dashboard
                      </a>
                      <a v-if="row.item.can_write !== 0" class="dropdown-item mt-2 mb-2" @click="duplicateDashboard(row.item, $event)" href="#">
                        <i class="tim-icons icon-single-copy-04 text-default mr-2"></i> Duplicate dashboard
                      </a>
                      <a v-if="row.item.can_write !== 0" class="dropdown-item mt-2 mb-2" @click="copyToGroupsPrompt(row.item, $event)" href="#">
                        <i class="tim-icons icon-world text-default mr-2"></i> Copy to other groups
                      </a>
                      <hr v-if="row.item.can_write !== 0">
                      <a v-if="row.item.can_write !== 0" class="dropdown-item mt-2 mb-2" @click="deleteDashboard(row.item, $event)" href="#">
                        <i class="tim-icons icon-trash-simple text-default mr-2"></i> Delete dashboard
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </template>
            </b-table>
          </div>
        </div>
      </card>
    </div>
  </div>

  <copy-dashboard-drawer
    :show="showCopyDrawer"
    :dashboard="selectedDashboard"
    :groups="allGroups"
    :source-group-id="sourceGroupId"
    :loading="copyLoading"
    @close="showCopyDrawer = false"
    @copy="copyToGroups"
  />

  <dashboard-template-drawer
    :show="showTemplateDrawer"
    :templates="templates"
    :loading="templateLoading"
    :list-loading="templatesListLoading"
    @close="showTemplateDrawer = false"
    @create="createFromTemplate"
  />

</div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import {BTable} from "bootstrap-vue";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";
import {Dropdown, DropdownMenu} from "element-ui";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import CopyDashboardDrawer from "@/pages/Dashboard/Dashboards/CopyDashboardDrawer.vue";
import DashboardTemplateDrawer from "@/pages/Dashboard/Dashboards/DashboardTemplateDrawer.vue";

export default {
  name: "ViewDashboards",
  components: {
    BTable,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    BreadcrumbItem,
    Breadcrumb,
    CopyDashboardDrawer,
    DashboardTemplateDrawer,
  },
  data() {
    return {
      loading: false,
      dashboards: [],
      showCopyDrawer: false,
      copyLoading: false,
      showTemplateDrawer: false,
      templateLoading: false,
      templatesListLoading: false,
      templates: [],
      allGroups: [],
      selectedDashboard: null,
    }
  },
  computed: {
    sourceGroupId(){
      return JSON.parse(localStorage.getItem('group')).id
    },
  },
  methods: {
    getLocalDateTime(d){
      if(!d) return ''
      const date = formatGroupDate(d, { dayOfWeek: false })
      const time = formatGroupTime(d)
      if(!date && !time) return ''
      return `${date}, ${time}`
    },
    editDashboard(item, event){
      event.preventDefault()
      this.$router.push({ path: '/dashboards/edit', query: { id:  item.id }})
    },
    async updateDashboard(item, event, action){
      event.preventDefault()
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
        + '/dashboards/' + item.id

      let data = {}

      if(action === 'pin'){
        data = {
          action: 'pin',
        }
      }

      this.axios.put(url, data, config)
        .then(response => {
          this.loading = false
          this.$notify({ type: 'success', message: 'Dashboard has been pinned', icon: 'tim-icons icon-check-2' })
          this.getDashboards()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if (err.response.status == 401) {
            this.$router.push('/forbidden')
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          }
        })
    },
    async deleteDashboard(item, event){
      event.preventDefault()

      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/' + item.id

      this.axios.delete(url, config)
        .then(response => {
          this.loading = false
          this.$notify({ type: 'success', message: 'Dashboard has been deleted', icon: 'tim-icons icon-check-2' })
          this.getDashboards()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else  if(err.response.status == 500){
            this.$notify({ type: 'danger', message: typeof err.response.data === 'string' ? err.response.data : 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          }
        })
    },
    openTemplateDrawer(){
      this.showTemplateDrawer = true
      this.getTemplates()
    },
    async getTemplates(){
      this.templatesListLoading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/dashboard-templates'

      try {
        let response = await this.axios.get(url, config)
        this.templates = response.data.templates || []
      } catch (err) {
        console.log(err)
        this.templates = []
        if(err.response && err.response.status === 401){
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: 'Could not load templates' })
        }
      } finally {
        this.templatesListLoading = false
      }
    },
    async createFromTemplate({ templateId, name, description }){
      // One endpoint for both paths: POST /dashboards, template_id optional. Without it the
      // response is the bare dashboard id (the original contract); with it, JSON
      // { dashboard_id, skipped }.
      this.templateLoading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards'

      let data = { name: name, description: description }
      if(templateId){
        data.template_id = templateId
      }

      try {
        let response = await this.axios.post(url, data, config)
        this.templateLoading = false
        this.showTemplateDrawer = false
        const newId = templateId ? response.data.dashboard_id : response.data
        this.$router.push({ path: '/dashboards/edit', query: { id: newId } })
      } catch (err) {
        console.log(err)
        this.templateLoading = false
        if(err.response && err.response.status === 401){
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: this.errorMessage(err, 'Could not create the dashboard') })
        }
      }
    },
    errorMessage(err, fallback){
      // New lambdas return JSON errors ({error}); older ones return plain strings.
      const data = err && err.response ? err.response.data : null
      if(!data) return fallback
      if(typeof data === 'object' && data.error) return data.error
      if(typeof data === 'string'){
        try {
          const parsed = JSON.parse(data)
          if(parsed && parsed.error) return parsed.error
        } catch (e) {
          return data
        }
      }
      return fallback
    },
    async duplicateDashboard(item, event){
      event.preventDefault()
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/' + item.id + '/duplicate'

      try {
        // Same-group duplicate: the current group is the only target.
        await this.axios.post(url, { groups: [companyGroupId] }, config)
        this.loading = false
        this.$notify({ type: 'success', message: 'Dashboard has been duplicated', icon: 'tim-icons icon-check-2' })
        this.getDashboards()
      } catch (err) {
        this.loading = false
        console.log(err)
        if(err.response && err.response.status == 401){
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        }
      }
    },
    async copyToGroupsPrompt(item, event){
      event.preventDefault()
      this.selectedDashboard = item
      await this.getGroups()
      this.showCopyDrawer = true
    },
    async getGroups(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups'
      try {
        let response = await this.axios.get(url, config)
        this.allGroups = response.data
      } catch (err) {
        console.log(err)
      }
    },
    async copyToGroups(groupIds){
      if(!groupIds || groupIds.length === 0 || !this.selectedDashboard){
        return
      }
      this.copyLoading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/' + this.selectedDashboard.id + '/duplicate'

      try {
        let response = await this.axios.post(url, { groups: groupIds }, config)
        let copied = response.data && response.data.copied ? response.data.copied : groupIds.length
        this.copyLoading = false
        this.showCopyDrawer = false
        this.$notify({ type: 'success', message: copied === 1 ? 'Dashboard has been copied to 1 group' : 'Dashboard has been copied to ' + copied + ' groups', icon: 'tim-icons icon-check-2' })
      } catch (err) {
        console.log(err)
        this.copyLoading = false
        if(err.response && err.response.status === 401){
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        }
      }
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getDashboards(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards'

      this.axios.get(url, config)
        .then(response => {
          this.loading = false
          this.dashboards = response.data.dashboards
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          }
        })
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
  },
  mounted() {
    if(!this.hasPermissions('dashboards_update')){
      this.$router.push('/dashboards')
    }
    this.getDashboards()
  }
}
</script>
